import React, { useEffect, useRef, useState } from 'react';
import {
    SESSION_EXPIRED_EVENT,
    SESSION_RESTORED_EVENT,
    getStoredUser,
    isSessionExpired,
    login,
    logout,
} from '../../utils/auth';
import './Banners.css';

/** Монтируется над маршрутами: открытая форма остаётся на месте со всем своим состоянием. */
const ReloginModal = () => {
    const [open, setOpen] = useState(isSessionExpired);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const dialogRef = useRef(null);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);

    useEffect(() => {
        const handleExpired = () => setOpen(true);
        const handleRestored = () => {
            setOpen(false);
            setPassword('');
            setError('');
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, handleExpired);
        window.addEventListener(SESSION_RESTORED_EVENT, handleRestored);
        return () => {
            window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpired);
            window.removeEventListener(SESSION_RESTORED_EVENT, handleRestored);
        };
    }, []);

    useEffect(() => {
        if (!open) return;
        // Сохранённый до перехода на логины пользователь содержит только email
        const storedUser = getStoredUser();
        const knownLogin = storedUser?.username || storedUser?.email || '';
        setUsername(knownLogin);
        (knownLogin ? passwordRef : usernameRef).current?.focus();
    }, [open]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const previousUserId = getStoredUser()?.id;
        try {
            setSubmitting(true);
            setError('');
            const { user } = await login(username.trim(), password);
            if (previousUserId && user?.id && user.id !== previousUserId) {
                // Открытая страница принадлежит другому пользователю
                localStorage.removeItem('selectedBranchId');
                window.location.assign('/dashboard');
            }
        } catch (err) {
            setError(err.message || 'Не удалось войти');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.assign('/login');
    };

    // Фокус не должен уходить на страницу под окном
    const handleKeyDown = (event) => {
        if (event.key !== 'Tab' || !dialogRef.current) return;
        const focusable = dialogRef.current.querySelectorAll('input, button:not([disabled])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    if (!open) return null;

    return (
        <div className="relogin-overlay">
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
                className="relogin-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="relogin-title"
                ref={dialogRef}
                onKeyDown={handleKeyDown}
            >
                <h2 id="relogin-title">Сессия истекла</h2>
                <p>Войдите снова, чтобы продолжить. Данные на странице останутся на месте.</p>
                <form onSubmit={handleSubmit}>
                    <label className="relogin-field">
                        Логин
                        <input
                            ref={usernameRef}
                            type="text"
                            name="username"
                            autoComplete="username"
                            autoCapitalize="none"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            required
                        />
                    </label>
                    <label className="relogin-field">
                        Пароль
                        <input
                            ref={passwordRef}
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            required
                        />
                    </label>
                    {error && <div className="relogin-error" role="alert">{error}</div>}
                    <div className="relogin-actions">
                        <button type="button" className="btn btn-secondary" onClick={handleLogout} disabled={submitting}>
                            Выйти
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? 'Вход...' : 'Войти'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReloginModal;
